import React from 'react';
import moment from 'moment';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';

import { SESSION_TYPES } from '../chat/slice/constants';
import MetricCard from './components/MetricCard';
import Sparkline from './components/Sparkline';
import { useAdminDashboardContext } from './hooks/useAdminDashboardContext';
import { useAdminDashboardSessions } from './hooks/useAdminDashboardSessions';
import { useAdminDashboardAnalytics } from './hooks/useAdminDashboardAnalytics';
import { useAdminDashboardUserDisplay } from './hooks/useAdminDashboardUserDisplay';
import { TIME_RANGES, TimeRangeKey } from './slice/constants';
import useSingleSessionViewHandler from '../../layouts/AdminLayout/hooks/useSingleSessionViewHandler';

export default function AdminDashboard() {
  const [range, setRange] = React.useState<TimeRangeKey>(TIME_RANGES.MONTH);
  const { currentProduct, currentUser } = useAdminDashboardContext();
  const { isLoading, error, chatSessions, callSessions, reload, latestTimeLineItems } = useAdminDashboardSessions({ currentProduct });
  const { initials, formatUserTitle } = useAdminDashboardUserDisplay();

  const {
    scopedAll,
    scopedChat,
    scopedCall,
    allDailyPoints,
   
    mostActive,
    totalSessionsDelta,
    chatsDelta,
    callsDelta,
    activeDays,
    callShare,
    allChartValues,
    chatChartValues,
    callChartValues,
    rangeLabel,
  } = useAdminDashboardAnalytics({ chatSessions, callSessions, range });
  const { setSelectedSessionIsdToView } = useSingleSessionViewHandler();
  return (
    <Box sx={{ width: '100%', overflowY: 'auto', height: '100dvh ', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 1280, mx: 'auto', width: '100%' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InsightsRoundedIcon color="primary" />
              Admin Dashboard
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
              <Chip size="small" label={currentProduct ?? '—'} />
              <Chip size="small" variant="outlined" label={rangeLabel} />
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <ToggleButtonGroup
              size="small"
              color="primary"
              value={range}
              exclusive
              onChange={(_, val) => {
                if (val) setRange(val);
              }}
            >
              <ToggleButton value={TIME_RANGES.TODAY}>{TIME_RANGES.TODAY}</ToggleButton>
              <ToggleButton value={TIME_RANGES.WEEK}>{TIME_RANGES.WEEK}</ToggleButton>
              <ToggleButton value={TIME_RANGES.MONTH}>{TIME_RANGES.MONTH}</ToggleButton>
            </ToggleButtonGroup>
            <IconButton onClick={reload} disabled={isLoading} aria-label="Refresh analytics">
              <RefreshRoundedIcon />
            </IconButton>
          </Stack>
        </Stack>

        {isLoading ? <LinearProgress sx={{ mt: 2, borderRadius: 999 }} /> : null}
        {error ? (
          <Typography variant="body2" sx={{ mt: 1.5 }} color="error">
            {error}
          </Typography>
        ) : null}

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          }}
        >
          <MetricCard title="Total sessions" value={`${scopedAll.length}`} subtitle={rangeLabel} deltaPct={totalSessionsDelta} icon={<AutoGraphRoundedIcon fontSize="small" />} trendValues={allChartValues} />
          <MetricCard title="Chats" value={`${scopedChat.length}`} subtitle="Conversations" deltaPct={chatsDelta} icon={<ChatBubbleOutlineRoundedIcon fontSize="small" />} trendValues={chatChartValues} />
          <MetricCard title="Calls" value={`${scopedCall.length}`} subtitle="Voice sessions" deltaPct={callsDelta} icon={<CallRoundedIcon fontSize="small" />} trendValues={callChartValues} />
          <MetricCard title="Active days" value={`${activeDays}`} subtitle="Days with activity" deltaPct={null} icon={<CalendarMonthRoundedIcon fontSize="small" />} trendValues={allChartValues} />
        </Box>

        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
            alignItems: 'stretch',
          }}
        >
          <Card>
            <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Box>
                  <Typography variant="h6">Activity trend</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sessions per day • {rangeLabel}
                  </Typography>
                </Box>
                <Chip size="small" variant="outlined" label={`Avg/day: ${((scopedAll.length || 0) / (range === '7d' ? 7 : range === '30d' ? 30 : 90)).toFixed(1)}`} />
              </Stack>

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Sparkline values={allChartValues} labels={allDailyPoints.map((p) => p.day)} width={520} height={96} />
              </Box>

              <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {allDailyPoints[0]?.label ?? ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {allDailyPoints[allDailyPoints.length - 1]?.label ?? ''}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
              <Typography variant="h6">Insights</Typography>
              <Typography variant="body2" color="text.secondary">
                Quick signals you can act on
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Calls share
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={callShare}
                      sx={{
                        height: 10,
                        borderRadius: 999,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': { borderRadius: 999 },
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, width: 44, textAlign: 'right' }}>
                    {callShare}%
                  </Typography>
                </Stack>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Most active day
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                    {mostActive ? `${mostActive.label} (${mostActive.value})` : '—'}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Total sessions
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {scopedAll.length}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Chats / Calls
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {scopedChat.length} / {scopedCall.length}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
            alignItems: 'stretch',
          }}
        >
          <Card>
            <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Box>
                  <Typography variant="h6">Recent activity</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Latest questions asked from Senta AI
                  </Typography>
                </Box>
                <Chip size="small" variant="outlined" label={`Showing ${Math.min(latestTimeLineItems.length, 10)}`} />
              </Stack>

              <Box sx={{ mt: 2 }}>
                {latestTimeLineItems.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No sessions found for this time range.
                  </Typography>
                ) : (
                  <Stack divider={<Divider flexItem />} spacing={1.25}>
                    {[ ...latestTimeLineItems].map((s) => {
                      const session: any = s.sessionId;
                      const isCall =  session? session.sessionType === SESSION_TYPES.CALL: true;
                      const title = s.userMessage;
                      return (
                        <Stack key={s._id} direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ py: 0.25 }}>
                          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                            <Chip size="small" color={isCall ? 'secondary' : 'primary'} variant="outlined" label={isCall ? 'Call' : 'Chat'} />
                            <Typography variant="body2"  sx={{ fontWeight: 600, cursor: 'pointer' }} noWrap onClick={() => setSelectedSessionIsdToView(session._id)}>
                              {title}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {moment(s.createdAt).format('MMM D • h:mm A')}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ height: 'fit-content' }}>
            <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
              <Typography variant="h6">User context</Typography>
              <Typography variant="body2" color="text.secondary">
                Who these analytics represent
              </Typography>

              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
                <Avatar sx={{ width: 44, height: 44 }}>{initials(currentUser?.name || currentUser?.email)}</Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 800 }} noWrap>
                    {formatUserTitle(currentUser)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {currentUser?.email ?? '—'}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Role
                  </Typography>
                  <Chip size="small" label={currentUser?.role ?? '—'} />
                </Stack>
                
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    User since
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                    {currentUser?.createdAt ? moment(currentUser.createdAt).format('MMM D, YYYY') : '—'}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
